import { ComputedFieldConfig, DataObject, DataType } from "../@types/DataObject";
import { Column, DatabaseType, TableSchema } from "../@types/Processor";
import DbConfig from "./DbConfig";

export class ComputedFieldProcessor {

  static async getComputedFields(name: string): Promise<any[]> {
    let computedFields = [];
    if (await DbConfig.ifExist()) {
      await DbConfig.connect()
      try {
        // Verificar si la tabla computes existe antes de intentar acceder a ella
        const tableExistsQuery = `SELECT name FROM sqlite_master WHERE type='table' AND name='dbcube_computes_config'`;
        const tableExistsResult = await DbConfig.query(tableExistsQuery);

        if (tableExistsResult.status === 'success' && tableExistsResult.data && tableExistsResult.data.length > 0) {
          const queryComputes = await DbConfig.query(`SELECT * FROM dbcube_computes_config WHERE database_ref='${name}'`);
          computedFields = queryComputes.data;
        } else {
          // La tabla no existe, inicializar como array vacío
          computedFields = [];
        }
      } catch (error) {
        console.error('Error fetching computed fields:', error);
        computedFields = [];
      }
      await DbConfig.disconnect()
    }

    return computedFields;
  }

  /**
   * Processes computed field instruction and returns the computed value
   * @param instruction - The @compute instruction
   * @param rowData - The row data containing column values
   * @returns The computed value or null if there's an error
   */
  static processInstruction(instruction: string, rowData: Record<string, any>): any {
    try {
      // Remove @compute wrapper and extract the function content
      const functionMatch = instruction.match(/@compute\s*\(\s*\(\s*\)\s*=>\s*{([\s\S]*?)}\s*\)/);

      if (!functionMatch) {
        throw new Error('Invalid @compute instruction format');
      }

      let functionBody: string = functionMatch[1].trim();

      // Clean up the function body - remove trailing semicolon if present
      if (functionBody.endsWith(';')) {
        functionBody = functionBody.slice(0, -1).trim();
      }

      // Replace @column(columnName) with actual values
      functionBody = functionBody.replace(/@column\(([^)]+)\)/g, (_match, columnName: string) => {
        const cleanColumnName = columnName.trim().replace(/['"]/g, '');
        const value = rowData[cleanColumnName];

        if (value === null || value === undefined) {
          return 'null';
        } else if (typeof value === 'string') {
          return `"${value.replace(/"/g, '\\"')}"`;
        } else if (typeof value === 'number' || typeof value === 'boolean') {
          return value.toString();
        } else if (value instanceof Date) {
          return `new Date("${value.toISOString()}")`;
        } else {
          return JSON.stringify(value);
        }
      });

      // Create and execute the function
      const computeFunction = new Function(functionBody);
      return computeFunction();
    } catch (error) {
      console.error('Error processing computed field:', error);
      return null;
    }
  }

  /**
   * Extracts column dependencies from a computed field instruction
   * @param instruction - The @compute instruction
   * @returns Array of column names that this computed field depends on
   */
  static extractDependencies(instruction: string): string[] {
    const dependencies: string[] = [];
    const columnMatches = instruction.match(/@column\(([^)]+)\)/g);

    if (columnMatches) {
      for (const match of columnMatches) {
        const innerMatch = match.match(/@column\(([^)]+)\)/);
        if (innerMatch) {
          const columnName = innerMatch[1].trim().replace(/['"]/g, '');
          if (!dependencies.includes(columnName)) {
            dependencies.push(columnName);
          }
        }
      }
    }

    return dependencies;
  }

  /**
   * Adds computed fields to an array of data objects based on configuration array
   * @param data - Array of data objects
   * @param computedConfigs - Array of computed field configurations
   * @returns Array with the new computed fields added
   */
  static computedFields<T extends DataObject>(
    data: T[],
    computedConfigs: ComputedFieldConfig[]
  ): T[] {

    return data.map((item, index) => {
      let processedItem: any = { ...item };

      // Procesar cada configuración de campo computado
      computedConfigs.forEach((config) => {
        try {
          const { column: fieldName, type, instruction } = config;

          // Reemplazar @column(fieldName) con el valor real del objeto
          let processedExpression = instruction.replace(
            /@column\(([^)]+)\)/g,
            (match, columnName) => {
              // Remover comillas si las tiene
              const cleanColumnName = columnName.replace(/['"]/g, '');
              const value = processedItem[cleanColumnName];

              // Convertir el valor a string seguro para la evaluación
              if (typeof value === 'string') {
                return `"${value}"`;
              } else if (value === null || value === undefined) {
                return 'null';
              } else {
                return String(value);
              }
            }
          );

          // Extraer la función dentro de @compute
          const computeMatch = processedExpression.match(/@compute\s*\(\s*\(\s*\)\s*=>\s*\{(.*)\}\s*\)/s);
          if (!computeMatch) {
            throw new Error(`Formato de @compute inválido para campo ${fieldName}`);
          }

          const functionBody = computeMatch[1];

          // Crear y ejecutar la función
          const computeFunction = new Function(functionBody);
          let result = computeFunction();

          // Aplicar conversión de tipo
          result = convertToType(result, type as DataType);

          // Agregar el nuevo campo al objeto procesado
          processedItem[fieldName] = result;

        } catch (error) {
          console.error(`Error procesando campo ${config.column} en item ${index}:`, error);
          // En caso de error, asignar null al campo
          processedItem[config.column] = null;
        }
      });

      return processedItem;
    });
  }
}

export class TableProcessor {
  static async generateAlterQueries(
    nowQuery: string,
    dbType: DatabaseType,
    tableName: string,
    database_ref: string,
  ): Promise<string[]> {
    function parseCreateTableQuery(query: string, dbType: DatabaseType): TableSchema {
      // Limpiar y normalizar la query
      const cleanQuery = query.trim().replace(/\s+/g, ' ');

      // Extraer el nombre de la tabla
      const tableNameMatch = cleanQuery.match(/CREATE TABLE (?:IF NOT EXISTS )?(\w+)/i);
      if (!tableNameMatch) {
        throw new Error('No se pudo extraer el nombre de la tabla');
      }

      const tableName = tableNameMatch[1];

      // Extraer las definiciones de columnas
      const columnsMatch = cleanQuery.match(/\((.+)\)/);
      if (!columnsMatch) {
        throw new Error('No se pudieron extraer las definiciones de columnas');
      }

      const columnsString = columnsMatch[1];
      const columnDefinitions = columnsString.split(',').map(def => def.trim());

      const columns: Column[] = [];

      for (const def of columnDefinitions) {
        // Skip constraints that aren't column definitions
        if (def.toUpperCase().startsWith('PRIMARY KEY') ||
          def.toUpperCase().startsWith('FOREIGN KEY') ||
          def.toUpperCase().startsWith('CONSTRAINT')) {
          continue;
        }

        const parts = def.split(/\s+/);
        if (parts.length < 2) continue;

        const columnName = parts[0];
        const columnType = parts[1];

        const column: Column = {
          name: columnName,
          type: columnType,
          nullable: !def.toUpperCase().includes('NOT NULL'),
          primaryKey: def.toUpperCase().includes('PRIMARY KEY'),
          autoIncrement: def.toUpperCase().includes('AUTOINCREMENT') || def.toUpperCase().includes('AUTO_INCREMENT')
        };

        columns.push(column);
      }

      return { tableName, columns };
    }

    function generateMySQLQueries(
      nowColumns: Map<string, Column>,
      oldColumns: Map<string, Column>,
      tableName: string
    ): string[] {
      const queries: string[] = [];

      // Columnas a eliminar
      for (const [columnName] of oldColumns) {
        if (!nowColumns.has(columnName)) {
          queries.push(`ALTER TABLE ${tableName} DROP COLUMN ${columnName};`);
        }
      }

      // Columnas a agregar o modificar
      for (const [columnName, column] of nowColumns) {
        const oldColumn = oldColumns.get(columnName);

        if (!oldColumn) {
          // Agregar nueva columna
          const columnDef = buildColumnDefinition(column, 'mysql');
          queries.push(`ALTER TABLE ${tableName} ADD COLUMN ${columnDef};`);
        } else if (!columnsEqual(column, oldColumn)) {
          // Modificar columna existente
          const columnDef = buildColumnDefinition(column, 'mysql');
          queries.push(`ALTER TABLE ${tableName} MODIFY COLUMN ${columnDef};`);
        }
      }

      return queries;
    }

    function generatePostgreSQLQueries(
      nowColumns: Map<string, Column>,
      oldColumns: Map<string, Column>,
      tableName: string
    ): string[] {
      const queries: string[] = [];

      // Columnas a eliminar
      for (const [columnName] of oldColumns) {
        if (!nowColumns.has(columnName)) {
          queries.push(`ALTER TABLE ${tableName} DROP COLUMN ${columnName};`);
        }
      }

      // Columnas a agregar o modificar
      for (const [columnName, column] of nowColumns) {
        const oldColumn = oldColumns.get(columnName);

        if (!oldColumn) {
          // Agregar nueva columna
          const columnDef = buildColumnDefinition(column, 'postgres');
          queries.push(`ALTER TABLE ${tableName} ADD COLUMN ${columnDef};`);
        } else if (!columnsEqual(column, oldColumn)) {
          // PostgreSQL requiere comandos separados para diferentes modificaciones
          if (column.type !== oldColumn.type) {
            const pgType = column.type.replace('INTEGER', 'INT').replace('TEXT', 'VARCHAR');
            queries.push(`ALTER TABLE ${tableName} ALTER COLUMN ${columnName} TYPE ${pgType};`);
          }

          if (column.nullable !== oldColumn.nullable) {
            const constraint = column.nullable ? 'DROP NOT NULL' : 'SET NOT NULL';
            queries.push(`ALTER TABLE ${tableName} ALTER COLUMN ${columnName} ${constraint};`);
          }
        }
      }

      return queries;
    }

    function generateSQLiteQueries(
      nowSchema: TableSchema,
      oldSchema: TableSchema,
      tableName: string
    ): string[] {
      // SQLite no soporta DROP COLUMN directamente, necesitamos recrear la tabla
      const queries: string[] = [];

      // Verificar si hay diferencias
      if (schemasEqual(nowSchema, oldSchema)) {
        return queries; // No hay cambios
      }

      const tempTableName = `${tableName}_temp_${Date.now()}`;

      // 1. Crear tabla temporal con el nuevo esquema
      const createTempQuery = buildCreateTableQuery(nowSchema, 'sqlite', tempTableName);
      queries.push(createTempQuery);

      // 2. Copiar datos compatibles
      const commonColumns = nowSchema.columns
        .filter(col => oldSchema.columns.some(oldCol => oldCol.name.toLowerCase() === col.name.toLowerCase()))
        .map(col => col.name);

      if (commonColumns.length > 0) {
        const columnsList = commonColumns.join(', ');
        queries.push(`INSERT INTO ${tempTableName} (${columnsList}) SELECT ${columnsList} FROM ${tableName};`);
      }

      // 3. Eliminar tabla original
      queries.push(`DROP TABLE ${tableName};`);

      // 4. Renombrar tabla temporal
      queries.push(`ALTER TABLE ${tempTableName} RENAME TO ${tableName};`);

      return queries;
    }

    function generateMongoDBQueries(
      nowColumns: Map<string, Column>,
      oldColumns: Map<string, Column>,
      collectionName: string
    ): string[] {
      const queries: string[] = [];

      // Campos a eliminar
      const fieldsToRemove: string[] = [];
      for (const [fieldName] of oldColumns) {
        if (!nowColumns.has(fieldName)) {
          fieldsToRemove.push(fieldName);
        }
      }

      if (fieldsToRemove.length > 0) {
        const unsetFields = fieldsToRemove.reduce((acc, field) => {
          acc[field] = "";
          return acc;
        }, {} as Record<string, string>);

        queries.push(`db.${collectionName}.updateMany({}, { $unset: ${JSON.stringify(unsetFields)} });`);
      }

      // Campos a agregar (con valores por defecto)
      const fieldsToAdd: Record<string, any> = {};
      for (const [fieldName, column] of nowColumns) {
        if (!oldColumns.has(fieldName)) {
          // Valor por defecto según el tipo
          let defaultValue: any = null;
          if (!column.nullable) {
            switch (column.type.toUpperCase()) {
              case 'INTEGER':
                defaultValue = 0;
                break;
              case 'TEXT':
              case 'VARCHAR':
                defaultValue = '';
                break;
              default:
                defaultValue = null;
            }
          }
          fieldsToAdd[fieldName] = defaultValue;
        }
      }

      if (Object.keys(fieldsToAdd).length > 0) {
        queries.push(`db.${collectionName}.updateMany({}, { $set: ${JSON.stringify(fieldsToAdd)} });`);
      }

      return queries;
    }

    function buildColumnDefinition(column: Column, dbType: DatabaseType): string {
      let def = `${column.name} ${column.type}`;

      if (column.primaryKey) {
        def += ' PRIMARY KEY';
      }

      if (column.autoIncrement) {
        if (dbType === 'mysql') {
          def += ' AUTO_INCREMENT';
        } else if (dbType === 'sqlite') {
          def += ' AUTOINCREMENT';
        } else if (dbType === 'postgres') {
          def = def.replace(column.type, 'SERIAL');
        }
      }

      if (!column.nullable) {
        def += ' NOT NULL';
      }

      return def;
    }

    function buildCreateTableQuery(schema: TableSchema, dbType: DatabaseType, tableName?: string): string {
      const name = tableName || schema.tableName;
      const columnDefs = schema.columns.map(col => buildColumnDefinition(col, dbType));
      return `CREATE TABLE ${name} (${columnDefs.join(', ')});`;
    }

    function columnsEqual(col1: Column, col2: Column): boolean {
      return col1.name.toLowerCase() === col2.name.toLowerCase() &&
        col1.type === col2.type &&
        col1.nullable === col2.nullable &&
        col1.primaryKey === col2.primaryKey &&
        col1.autoIncrement === col2.autoIncrement;
    }

    function schemasEqual(schema1: TableSchema, schema2: TableSchema): boolean {
      if (schema1.columns.length !== schema2.columns.length) {
        return false;
      }

      const cols1 = new Map(schema1.columns.map(col => [col.name.toLowerCase(), col]));
      const cols2 = new Map(schema2.columns.map(col => [col.name.toLowerCase(), col]));

      for (const [name, col1] of cols1) {
        const col2 = cols2.get(name);
        if (!col2 || !columnsEqual(col1, col2)) {
          return false;
        }
      }

      return true;
    }

    if (await DbConfig.ifExist()) {
      await DbConfig.connect()
      try {
        // Verificar si la tabla computes existe antes de intentar acceder a ella
        const tableExistsQuery = `SELECT name FROM sqlite_master WHERE type='table' AND name='dbcube_computes_config'`;
        const tableExistsResult = await DbConfig.query(tableExistsQuery);

        if (tableExistsResult.status === 'success' && tableExistsResult.data && tableExistsResult.data.length > 0) {
          const queryComputes = await DbConfig.query(`SELECT * FROM dbcube_schemas_config WHERE table_ref='${tableName}' AND database_ref='${database_ref}'`);
          const oldQuery = queryComputes.data[0];

          // Validar que oldQuery existe y tiene struct
          if (!oldQuery || !oldQuery.struct) {
            console.error('No exist a previus schema please execute the refresh first...', oldQuery);
            return [];
          }

          const nowSchema = parseCreateTableQuery(nowQuery, dbType);
          const oldSchema = parseCreateTableQuery(oldQuery.struct, dbType);

          // Verificar que el nombre de tabla coincida
          if (nowSchema.tableName.toLowerCase() !== tableName.toLowerCase()) {
            throw new Error(`El nombre de tabla en la query (${nowSchema.tableName}) no coincide con el parámetro (${tableName})`);
          }

          // Crear mapas para facilitar la comparación
          const nowColumns = new Map(nowSchema.columns.map(col => [col.name.toLowerCase(), col]));
          const oldColumns = new Map(oldSchema.columns.map(col => [col.name.toLowerCase(), col]));

          switch (dbType) {
            case 'mysql':
              return generateMySQLQueries(nowColumns, oldColumns, tableName);

            case 'postgres':
              return generatePostgreSQLQueries(nowColumns, oldColumns, tableName);

            case 'sqlite':
              return generateSQLiteQueries(nowSchema, oldSchema, tableName);

            case 'mongodb':
              return generateMongoDBQueries(nowColumns, oldColumns, tableName);

            default:
              throw new Error(`Tipo de base de datos no soportado: ${dbType}`);
          }
        }
      } catch (error) {
        console.error('Error fetching computed fields:', error);
      }
      await DbConfig.disconnect()
      return [nowQuery];
    } else {
      return [nowQuery];
    }
  }

  static async saveQuery(table_ref: string, database_ref: string, struct: string): Promise<void> {

    if (await DbConfig.ifExist()) {
      await DbConfig.connect()
      try {
        await DbConfig.query(`DELETE FROM dbcube_schemas_config WHERE table_ref='${table_ref}' AND database_ref='${database_ref}'`);

        // Verificar si la tabla computes existe antes de intentar acceder a ella
        const tableExistsQuery = `INSERT INTO dbcube_schemas_config (table_ref, database_ref, struct) VALUES (?, ?, ?)`;
        await DbConfig.queryWithParameters(tableExistsQuery, [table_ref, database_ref, struct]);
      } catch (error) {
        console.error('Error fetching computed fields:', error);
      }
      await DbConfig.disconnect()
    }
  }
}

export class TriggerProcessor {

  static async getTriggers(name: string): Promise<any[]> {
    let triggers = [];
    if (await DbConfig.ifExist()) {
      await DbConfig.connect()
      try {
        // Verificar si la tabla computes existe antes de intentar acceder a ella
        const tableExistsQuery = `SELECT name FROM sqlite_master WHERE type='table' AND name='dbcube_triggers_config'`;
        const tableExistsResult = await DbConfig.query(tableExistsQuery);

        if (tableExistsResult.status === 'success' && tableExistsResult.data && tableExistsResult.data.length > 0) {
          const queryComputes = await DbConfig.query(`SELECT * FROM dbcube_triggers_config WHERE database_ref='${name}'`);
          triggers = queryComputes.data;
        } else {
          // La tabla no existe, inicializar como array vacío
          triggers = [];
        }
      } catch (error) {
        console.error('Error fetching computed fields:', error);
        triggers = [];
      }
      await DbConfig.disconnect()
    }

    return triggers;
  }
}

// Utils
function convertToType(value: any, type: DataType): any {
  switch (type) {
    case 'string':
      return String(value);
    case 'number':
      const num = Number(value);
      return isNaN(num) ? 0 : num;
    case 'boolean':
      return Boolean(value);
    case 'date':
      return value instanceof Date ? value : new Date(value);
    case 'object':
      return value;
    default:
      return value;
  }
}